import { Group, Stack, Text, ThemeIcon } from '@mantine/core'
import {
  IconCheck,
  IconCpu,
  IconHourglassHigh,
  IconX
} from '@tabler/icons-react'
import { FormattedMessage } from 'react-intl'
import { DeploymentGroupEngine } from '@/types/deploymentGroup'
import { formatDuration, getFormattedDateWithTime } from '@/utils/date'
import { getEngineErrorMessageId } from './groupErrorMessages'

type EngineBuildStatusProps = {
  engine: DeploymentGroupEngine
  gpuIndex?: number | null
}

const StateIcon = ({ state }: { state: DeploymentGroupEngine['state'] }) => {
  if (state === 'ready') {
    return (
      <ThemeIcon color="green" radius="xl" size="sm">
        <IconCheck style={{ width: '70%', height: '70%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  if (state === 'failed') {
    return (
      <ThemeIcon color="red" radius="xl" size="sm">
        <IconX style={{ width: '70%', height: '70%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  if (state === 'building') {
    return (
      <ThemeIcon variant="light" radius="xl" size="sm">
        <IconCpu style={{ width: '80%', height: '80%' }} stroke={2} />
      </ThemeIcon>
    )
  }

  return (
    <ThemeIcon variant="light" radius="xl" size="sm">
      <IconHourglassHigh style={{ width: '80%', height: '80%' }} stroke={2} />
    </ThemeIcon>
  )
}

const StateText = ({ engine, gpuIndex }: EngineBuildStatusProps) => {
  const { state, queue_position, error_code } = engine

  if (state === null) {
    return <FormattedMessage id="deployments.groups.engine.notReported" />
  }

  if (state === 'queued') {
    return queue_position !== null ? (
      <FormattedMessage
        id="deployments.groups.engine.queued"
        values={{ position: queue_position }}
      />
    ) : (
      <FormattedMessage id="deployments.groups.engine.queued.noPosition" />
    )
  }

  if (state === 'building') {
    return typeof gpuIndex === 'number' ? (
      <FormattedMessage
        id="deployments.groups.engine.building"
        values={{ index: gpuIndex }}
      />
    ) : (
      <FormattedMessage id="deployments.groups.engine.building.noGpu" />
    )
  }

  if (state === 'ready') {
    return <FormattedMessage id="deployments.groups.engine.ready" />
  }

  const errorMessageId = getEngineErrorMessageId(error_code)

  if (errorMessageId) {
    return <FormattedMessage id={errorMessageId} />
  }

  return error_code ? (
    <FormattedMessage
      id="deployments.groups.engine.failed.withCode"
      values={{ code: error_code }}
    />
  ) : (
    <FormattedMessage id="deployments.groups.engine.failed" />
  )
}

export const EngineBuildStatus = ({
  engine,
  gpuIndex
}: EngineBuildStatusProps) => {
  const showBuildDetails = engine.state === 'ready'

  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <StateIcon state={engine.state} />

      <Stack gap={2}>
        <Text size="sm">
          <StateText engine={engine} gpuIndex={gpuIndex} />
        </Text>

        {showBuildDetails && engine.built_at && (
          <Text size="xs" c="dimmed">
            <FormattedMessage
              id="deployments.groups.engine.ready.builtAt"
              values={{ builtAt: getFormattedDateWithTime(engine.built_at) }}
            />
          </Text>
        )}

        {showBuildDetails && typeof engine.build_s === 'number' && (
          <Text size="xs" c="dimmed">
            <FormattedMessage
              id="deployments.groups.engine.ready.buildTime"
              values={{ duration: formatDuration(engine.build_s * 1000) }}
            />
          </Text>
        )}
      </Stack>
    </Group>
  )
}
